import LOCALS from 'commons/locals';
import { FormLabel } from '../form-label';
import i18n from 'i18n';
import { Control, Controller } from 'react-hook-form';
import { CreateProductFormSchemaType } from '../common';
import { MousePointer, Plus, Trash2 } from 'lucide-react';
import commonApi from 'apis/common';
import { uniqBy } from 'lodash-es';
import { Button, Progress } from 'antd';
import { Image } from 'antd';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import { arrayMoveImmutable as arrayMove } from 'array-move';
import { useEffect, useRef, useState } from 'react';
import { useToggle } from 'react-use';
import { ImageListToolbar, getVisibleImageItems } from './image-list-toolbar';

// 拖拽手柄组件
const DragHandle = SortableHandle(() => (
  <Button
    className="flex items-center justify-center"
    icon={<MousePointer className="w-4 h-4" />}
  />
));

// 可排序的图片项组件
const SortableImageItem = SortableElement(
  (props: {
    imageKey: string;
    onDelete: (imageKey: string) => void;
    isDisabled: boolean;
  }) => {
    const { imageKey, onDelete, isDisabled } = props;
    const [imageUrl, setImageUrl] = useState<string>('');

    useEffect(() => {
      commonApi.getSignedFileUrl(imageKey).then(({ url }) => {
        setImageUrl(url);
      });
    }, [imageKey]);

    return (
      <div className="w-40 relative rounded cursor-pointer space-y-2">
        <Image
          src={imageUrl}
          alt=""
          className="w-full aspect-square object-cover rounded"
        />

        {!isDisabled && (
          <div className="flex gap-2 justify-between">
            <DragHandle />
            <Button
              danger
              className="flex items-center justify-center"
              icon={<Trash2 className="w-4 h-4" />}
              onClick={() => onDelete(imageKey)}
            />
          </div>
        )}
      </div>
    );
  },
);

// 可排序的容器组件
const SortableImageList = SortableContainer(
  (props: {
    items: string[];
    onDelete: (imageKey: string) => void;
    isDisabled: boolean;
  }) => {
    const { items, onDelete, isDisabled } = props;

    return (
      <div className="flex gap-4 flex-wrap">
        <Image.PreviewGroup>
          {items.map((imageKey: string, index: number) => (
            <SortableImageItem
              key={imageKey}
              index={index}
              {...{
                imageKey,
                onDelete,
                isDisabled,
              }}
            />
          ))}
        </Image.PreviewGroup>
      </div>
    );
  },
);

const InternalImages = ({
  control,
  productId,
  disabled,
  onUploadingChange,
}: {
  control: Control<CreateProductFormSchemaType>;
  productId?: number;
  disabled: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}) => {
  const [isDraggingOver, toggleDraggingOver] = useToggle(false);
  const [expanded, toggleExpanded] = useToggle(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const progressMapRef = useRef<
    Record<string, { loaded: number; total?: number }>
  >({});

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  useEffect(() => {
    return () => onUploadingChange?.(false);
  }, [onUploadingChange]);

  return (
    <div className="flex gap-2">
      <FormLabel className="basis-[12.5%]">
        {i18n.t(LOCALS.product_internal_images)}:
      </FormLabel>
      <div className="w-full">
        <Controller
          name="internalImages"
          control={control}
          render={({ field }) => {
            // 统一添加图片逻辑，供拖拽与 input 共用
            const handleAddFiles = async (filesLike: FileList | File[]) => {
              const files = Array.from(filesLike || []);
              if (!files.length) return;

              // 初始化进度
              progressMapRef.current = {};
              setIsUploading(true);
              setUploadPercent(0);

              const calcOverallPercent = () => {
                const entries = Object.values(progressMapRef.current);
                if (!entries.length) return 0;

                const totalKnown = entries.every(
                  (d) => typeof d.total === 'number',
                );
                if (totalKnown) {
                  const sumLoaded = entries.reduce(
                    (s, d) => s + (d.loaded || 0),
                    0,
                  );
                  const sumTotal = entries.reduce(
                    (s, d) => s + (d.total || 0),
                    0,
                  );
                  if (!sumTotal) return 0;
                  return Math.min(
                    100,
                    Math.max(0, Math.round((sumLoaded / sumTotal) * 100)),
                  );
                }

                // 如果拿不到 total，就用平均百分比兜底
                const percents = entries.map((d) => {
                  if (!d.total) return 0;
                  return Math.min(
                    100,
                    Math.max(0, Math.round((d.loaded / d.total) * 100)),
                  );
                });
                const avg =
                  percents.reduce((s, p) => s + p, 0) / percents.length;
                return Math.round(avg);
              };

              try {
                const uploadPromises = files.map((file) => {
                  const key = `${file.name}_${file.size}_${file.lastModified}`;
                  progressMapRef.current[key] = { loaded: 0, total: file.size };
                  const onProgress = ({
                    loaded,
                    total,
                  }: {
                    loaded: number;
                    total?: number;
                  }) => {
                    progressMapRef.current[key] = {
                      loaded: loaded ?? 0,
                      total: total ?? progressMapRef.current[key]?.total,
                    };
                    setUploadPercent(calcOverallPercent());
                  };

                  return commonApi.uploadFile(file, true, {
                    onProgress,
                    productId,
                  });
                });

                const results = await Promise.all(uploadPromises);
                setUploadPercent(100);

                const newList = [
                  ...(field.value || []),
                  ...results.map((d) => d.key),
                ];
                const uniqueList = uniqBy(newList, (d) => d);
                field.onChange(uniqueList);
                setIsUploading(false);
                setUploadPercent(0);
              } catch (e) {
                setIsUploading(false);
                setUploadPercent(0);
                throw e;
              }
            };

            const handleSortEnd = ({
              oldIndex,
              newIndex,
            }: {
              oldIndex: number;
              newIndex: number;
            }) => {
              if (!field.value) return;
              const newOrder = arrayMove(field.value, oldIndex, newIndex);
              field.onChange(newOrder);
            };

            const handleDelete = (imageKey: string) => {
              field.onChange(field.value?.filter((d) => d !== imageKey));
            };

            const items = field.value || [];
            const visibleItems = getVisibleImageItems(items, expanded);

            return (
              <div>
                <ImageListToolbar
                  count={items.length}
                  expanded={expanded}
                  onToggle={toggleExpanded}
                />
                <div className="flex gap-4 flex-wrap">
                  {visibleItems.length > 0 && (
                    <SortableImageList
                      {...{
                        items: visibleItems,
                        onDelete: handleDelete,
                        onSortEnd: handleSortEnd,
                        axis: 'xy',
                        useDragHandle: true,
                        isDisabled: disabled,
                      }}
                    />
                  )}

                  {!disabled && (
                    <label
                      className={`relative flex justify-center items-center w-40 h-40 border border-dashed rounded cursor-pointer border-gray-400 hover:border-gray-500 hover:border-2 ${
                        isDraggingOver ? 'bg-gray-50 border-blue-400' : ''
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (!isDraggingOver) toggleDraggingOver(true);
                      }}
                      onDragLeave={() => {
                        if (isDraggingOver) toggleDraggingOver(false);
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        if (isDraggingOver) toggleDraggingOver(false);
                        const acceptTypes = [
                          'image/jpeg',
                          'image/png',
                          'image/jpg',
                          'image/webp',
                        ];
                        const files = Array.from(
                          e.dataTransfer?.files || [],
                        ).filter((f) => acceptTypes.includes(f.type));
                        await handleAddFiles(files);
                      }}
                    >
                      {isUploading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Progress
                            type="circle"
                            size={56}
                            percent={uploadPercent}
                            showInfo
                            status={uploadPercent >= 100 ? 'success' : 'active'}
                          />
                        </div>
                      ) : (
                        <Plus className="w-6 h-6 text-gray-400" />
                      )}
                      <input
                        type="file"
                        className="absolute opacity-0 pointer-events-none"
                        multiple
                        accept="image/jpeg, image/png, image/jpg, image/webp"
                        disabled={isUploading}
                        onChange={async (e) => {
                          await handleAddFiles(e.target.files as FileList);
                          // 清空 input 值，允许重复选择同一文件
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

export default InternalImages;
